import React, { useState, useEffect } from 'react';
import { FiMousePointer, FiTrendingUp, FiUsers, FiCheckCircle } from 'react-icons/fi';
import { ownerAnalyticsAPI } from '../../../api/ownerEndpoints';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../components/common/Toaster';
import { useTheme } from '../../../contexts/ThemeContext';
import { safeParseFloat, safeToFixed } from './utils';
import {
  Section,
  SectionTitle,
  Grid,
  StatCard,
  StatLabel,
  StatValue,
  StatDescription,
  Card,
  LoadingContainer,
  EmptyState,
  ProgressBar,
  ProgressFill,
} from './Analytics.styles';

const Funnel = ({ startDate, endDate }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { 
        start_date: startDate ? `${startDate}T00:00:00Z` : undefined,
        end_date: endDate ? `${endDate}T23:59:59Z` : undefined,
      };
      const response = await ownerAnalyticsAPI.getFunnel(params);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching funnel:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="large" text="Loading conversion funnel..." />
      </LoadingContainer>
    );
  }

  if (!data) {
    return (
      <EmptyState>
        <FiMousePointer size={64} />
        <h3>No Funnel Data</h3>
        <p>Conversion funnel metrics will appear when available</p>
      </EmptyState>
    );
  }

  const funnel = data.funnel || {};

  return (
    <>
      <Section>
        <SectionTitle>
          <FiMousePointer />
          Conversion Funnel
        </SectionTitle>
        <Card>
          <div style={{ padding: theme.spacing.lg }}>
            {/* Signups */}
            <div style={{ marginBottom: theme.spacing.xl }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing.sm }}>
                <div>
                  <h3 style={{ margin: 0, color: theme.colors.textPrimary, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <FiUsers /> Signups
                  </h3>
                </div>
                <StatValue>{safeParseFloat(funnel.signups).toLocaleString()}</StatValue>
              </div>
              <ProgressBar>
                <ProgressFill width={100} color={theme.colors.primary} />
              </ProgressBar>
            </div>

            {/* Active Users */}
            <div style={{ marginBottom: theme.spacing.xl }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing.sm }}>
                <div>
                  <h3 style={{ margin: 0, color: theme.colors.textPrimary, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <FiCheckCircle /> Active Users
                  </h3>
                  <StatDescription>Signup to Active: {safeToFixed(funnel.signup_to_active_pct, 1)}%</StatDescription>
                </div>
                <StatValue>{safeParseFloat(funnel.active).toLocaleString()}</StatValue>
              </div>
              <ProgressBar>
                <ProgressFill width={funnel.signup_to_active_pct || 0} color={theme.colors.secondary} />
              </ProgressBar>
            </div>

            {/* Purchasers */}
            <div style={{ marginBottom: theme.spacing.xl }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing.sm }}>
                <div>
                  <h3 style={{ margin: 0, color: theme.colors.textPrimary, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <FiTrendingUp /> Purchasers
                  </h3>
                  <StatDescription>Active to Purchaser: {safeToFixed(funnel.active_to_purchaser_pct, 1)}%</StatDescription>
                </div>
                <StatValue>{safeParseFloat(funnel.purchasers).toLocaleString()}</StatValue>
              </div>
              <ProgressBar>
                <ProgressFill width={funnel.active_to_purchaser_pct || 0} color={theme.colors.accent} />
              </ProgressBar>
            </div>

            {/* Overall Conversion */}
            <div style={{ marginTop: theme.spacing.xl, padding: theme.spacing.lg, background: theme.colors.backgroundSecondary, borderRadius: theme.borderRadius.md }}>
              <StatDescription style={{ marginBottom: theme.spacing.sm }}>
                Overall Conversion Rate (Signup to Purchase)
              </StatDescription>
              <StatValue style={{ fontSize: '32px', color: theme.colors.primary }}>
                {safeToFixed(((funnel.active_to_purchaser_pct || 0) * (funnel.signup_to_active_pct || 0) / 100), 1)}%
              </StatValue>
            </div>
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Key Metrics</SectionTitle>
        <Grid minWidth="200px">
          <StatCard>
            <StatLabel>Conversion Rate</StatLabel>
            <StatValue>{safeToFixed(funnel.active_to_purchaser_pct, 1)}%</StatValue>
            <StatDescription>Active to Purchaser</StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Engagement Rate</StatLabel>
            <StatValue>{safeToFixed(funnel.signup_to_active_pct, 1)}%</StatValue>
            <StatDescription>Signup to Active</StatDescription>
          </StatCard>
        </Grid>
      </Section>
    </>
  );
};

export default Funnel;

